<script setup lang="ts">
import type { ExclusionProviderResponse, PaAPI } from '@rialtic/types'
import { ClaimType } from '@rialtic/types'
import { formatDisplayDate } from '#imports'
import {
  capitalize,
  claimTypeLabels,
  claimTypeMap,
  type ClaimTypeFlags,
} from '@rialtic/utils'
import { useWorkspace } from '~/stores/workspace'

const props = defineProps<{
  exclusion: Exclusion
  pending?: boolean
}>()

const workspace = useWorkspace()
const workersApi = useWorkersApi()
const { $groundcover } = useNuxtApp()

const sortedExclusionVersionConnectors = computed(() =>
  [...(props.exclusion?.exclusion_version_connectors || [])].sort((a, b) =>
    a.connector.name.localeCompare(b.connector.name),
  ),
)

const claimTypeFlags = workspace.getClaimTypeFeatureFlags

const displayedClaimTypes = computed<string[]>(() => {
  const exclusion = props.exclusion
  if (!exclusion || !claimTypeFlags) return []

  const labels = claimTypeLabels(exclusion as ClaimTypeFlags)
  const claimTypeFlagsMap = claimTypeFlags.map((flag) => claimTypeMap[flag])

  return labels.filter(
    (label) =>
      claimTypeFlagsMap.includes(label as ClaimType) ||
      label === ClaimType.AllClaimTypes,
  )
})

const diagnosisList = computed(() =>
  props.exclusion?.diagnoses?.map((diagnosis) => diagnosis.diagnosis),
)

const editTypesList = computed(() =>
  props.exclusion?.edit_types?.map((et) => et.edit_type),
)

const procedureCodeList = computed(() =>
  props.exclusion?.procedures ? props.exclusion.procedures : [],
)

const providerSpecialtyCodesList = computed(() => props.exclusion?.specialties)

const getProviderNamesForNPIs = async (codes: string[]) => {
  if (!codes.length) return []

  try {
    const response = await workersApi.fetch<{
      providers: ExclusionProviderResponse[]
    }>('/npi/names', {
      method: 'POST',
      body: { codes },
    })
    return response.providers
  } catch (error) {
    $groundcover?.captureException(error as Error)
    return codes.map((code) => ({
      code: code,
      name: '',
      type: 'NPI' as const,
    }))
  }
}

const getProviderNamesForTINs = async (codes: string[]) => {
  if (!codes.length) return []

  try {
    const providerData = await workersApi.fetch<PaAPI.Get['/provider/name']>(
      '/provider/name',
      {
        params: {
          npi: [],
          tin: codes,
        },
      },
    )

    return codes.map((code) => {
      const existingProvider = providerData.find((p) => p.code === code)
      return (
        existingProvider || {
          code: code,
          name: '',
          type: 'TIN' as const,
        }
      )
    })
  } catch (error) {
    $groundcover?.captureException(error as Error)
    return codes.map((code) => ({
      code: code,
      name: '',
      type: 'TIN' as const,
    }))
  }
}

const providersByTIN = computedAsync(async () => {
  if (!props.exclusion?.provider_tins?.length) return []
  return await getProviderNamesForTINs(props.exclusion.provider_tins)
}, [])

const providersByNPI = computedAsync(async () => {
  if (!props.exclusion?.provider_npis?.length) return []
  return await getProviderNamesForNPIs(props.exclusion.provider_npis)
}, [])

const providersByAltID = computed(() => props.exclusion?.provider_alt_ids)
const providersByContractID = computed(() => props.exclusion?.contract_ids)
const providersByNetworkID = computed(() => props.exclusion?.network_ids)

const policiesList = computed(() =>
  props.exclusion?.policies?.map((policy) => ({
    id: policy.policy.id,
    name: policy.policy.name,
  })),
)

const claimLineType = computed(() => {
  if (props.exclusion?.claim_line_type === 'GROUP_ID') {
    return 'Member Group'
  }
  return props.exclusion?.claim_line_type
    ? capitalize(props.exclusion.claim_line_type)
    : ''
})

const posList = computed(() =>
  props.exclusion?.places_of_service?.flatMap((pos) => pos.place_of_service),
)

const pricingMethodologyCodesList = computed(() =>
  props.exclusion?.pricing_methodology_codes?.map((item: any) => ({
    code: item.code || item.pricing_methodology_code,
  })),
)

const typeOfBillCodesList = computed(() =>
  props.exclusion?.type_of_bill_codes?.map((item: any) => ({
    code: item.code || item.type_of_bill_code,
  })),
)

const revenueCodesList = computed(() =>
  props.exclusion?.revenue_codes?.map((item: any) => ({
    code: item.code || item.revenue_code,
  })),
)

const conditionCodesList = computed(() =>
  props.exclusion?.condition_codes?.map((item: any) => ({
    code: item.code || item.condition_code,
  })),
)
</script>

<template>
  <section class="mb-8 flex flex-col space-y-2">
    <div class="rounded-lg p-5 pl-0">
      <div>
        <UiRichTextBlock
          :custom="true"
          :content="exclusion?.rationale"
          :transparent="true"
          title="Rationale"
        />
      </div>
      <div class="flex space-x-4">
        <div class="subtitle-2 text-primary-900 mb-3 w-32">
          Rationale category
        </div>
        <div class="body-2 text-neutral-900">
          {{ pending ? '' : exclusion?.category?.name || 'None' }}
        </div>
      </div>
      <div class="flex space-x-4">
        <div class="subtitle-2 text-primary-900 mb-3 w-32">Exclusion type</div>
        <div class="body-2 text-neutral-900">
          {{ pending ? '' : claimLineType || 'None' }}
        </div>
      </div>
      <div class="flex space-x-4">
        <div class="subtitle-2 text-primary-900 mb-3 w-32">Date type</div>
        <div class="body-2 text-neutral-900">
          {{
            exclusion?.effective_by == 'DATE_OF_SERVICE'
              ? 'Exclude by claim line date of service'
              : 'Exclude by date processed by Rialtic'
          }}
        </div>
      </div>
      <div class="flex space-x-4">
        <div class="subtitle-2 text-primary-900 mb-3 w-32">
          Effective start date
        </div>
        <div class="body-2 text-neutral-900">
          {{ formatDisplayDate(exclusion?.effective_date_start) || '-' }}
        </div>
      </div>
      <div class="flex space-x-4">
        <div class="subtitle-2 text-primary-900 mb-3 w-32">
          Effective end date
        </div>
        <div class="body-2 text-neutral-900">
          {{ formatDisplayDate(exclusion?.effective_date_end) || '-' }}
        </div>
      </div>
      <div class="flex space-x-4">
        <div class="subtitle-2 text-primary-900 w-32">Line(s) of business</div>
        <div
          v-if="exclusion?.is_all_connectors"
          class="body-2 text-neutral-900"
        >
          All
        </div>
        <div
          v-else
          class="body-2 grid grid-cols-[max-content_max-content] items-center gap-x-4 gap-y-2 text-neutral-900"
        >
          <template
            v-for="exclusionVersionConnector in sortedExclusionVersionConnectors"
            :key="exclusionVersionConnector.connector_id"
          >
            <div>{{ exclusionVersionConnector.connector.name }}</div>
            <div>
              <ExclusionStatusBadge
                v-if="exclusionVersionConnector.status != 'Draft'"
                :status="exclusionVersionConnector.status"
              />
            </div>
          </template>
        </div>
      </div>
    </div>

    <div
      class="flex w-full flex-col divide-y divide-neutral-200 overflow-hidden rounded-lg border border-neutral-200 bg-neutral-50"
    >
      <UiListItemExpand v-if="exclusion?.claim_group_ids?.length">
        <span class="text-sm">
          Excluded Member Group ID{{
            exclusion?.claim_group_ids?.length > 1 ? 's' : ''
          }}({{ exclusion?.claim_group_ids?.length }})
        </span>

        <template #content>
          <div class="body-2 p-5 text-neutral-900">
            <ul class="body-2">
              <li v-for="id in exclusion?.claim_group_ids" class="py-1">
                {{ id }}
              </li>
            </ul>
          </div>
        </template>
      </UiListItemExpand>
      <UiListItemExpand v-if="displayedClaimTypes.length">
        <span class="text-sm">
          Excluded Claim Types ({{
            displayedClaimTypes.includes(ClaimType.AllClaimTypes)
              ? 'Any'
              : displayedClaimTypes.length
          }})
        </span>
        <template #content>
          <div class="body-2 p-5 text-neutral-900">
            <ul class="body-2">
              <li v-for="type in displayedClaimTypes" class="py-1">
                {{ type }}
              </li>
            </ul>
          </div>
        </template>
      </UiListItemExpand>
      <UiListItemExpand v-if="providersByTIN?.length">
        <span class="text-sm">
          Excluded Provider{{ providersByTIN?.length > 1 ? 's' : '' }} by TIN
          ({{ providersByTIN.length }})
        </span>

        <template #content>
          <div class="body-2 p-5 text-neutral-900">
            <ul v-if="exclusion && providersByTIN?.length" class="body-2">
              <li v-for="provider in providersByTIN" class="py-1">
                {{ provider.code }}
                {{ provider.name ? `- ${provider.name}` : '' }}
              </li>
            </ul>
          </div>
        </template>
      </UiListItemExpand>
      <UiListItemExpand v-if="providersByNPI?.length">
        <span class="text-sm">
          Excluded Provider{{ providersByNPI?.length > 1 ? 's' : '' }} by NPI
          ({{ providersByNPI.length }})
        </span>

        <template #content>
          <div class="body-2 p-5 text-neutral-900">
            <ul v-if="exclusion && providersByNPI?.length" class="body-2">
              <li v-for="provider in providersByNPI" class="py-1">
                {{ provider.code }}
                {{ provider.name ? `- ${provider.name}` : '' }}
              </li>
            </ul>
          </div>
        </template>
      </UiListItemExpand>
      <UiListItemExpand v-if="providersByAltID?.length">
        <span class="text-sm">
          Excluded Provider{{ providersByAltID?.length > 1 ? 's' : '' }} by Alt
          ID ({{ providersByAltID?.length }})
        </span>

        <template #content>
          <div class="body-2 p-5 text-neutral-900">
            <ul v-if="exclusion && providersByAltID?.length" class="body-2">
              <li v-for="id in providersByAltID" class="py-1">
                {{ id }}
              </li>
            </ul>
          </div>
        </template>
      </UiListItemExpand>
      <UiListItemExpand v-if="providersByContractID?.length">
        <span class="text-sm">
          Excluded Provider{{ providersByContractID?.length > 1 ? 's' : '' }} by
          Contract ID ({{ providersByContractID?.length }})
        </span>

        <template #content>
          <div class="body-2 p-5 text-neutral-900">
            <ul
              v-if="exclusion && providersByContractID?.length"
              class="body-2"
            >
              <li v-for="id in providersByContractID" class="py-1">
                {{ id }}
              </li>
            </ul>
          </div>
        </template>
      </UiListItemExpand>
      <UiListItemExpand v-if="providersByNetworkID?.length">
        <span class="text-sm">
          Excluded Provider{{ providersByNetworkID.length > 1 ? 's' : '' }} by
          Network ID ({{ providersByNetworkID.length }})
        </span>

        <template #content>
          <div class="body-2 p-5 text-neutral-900">
            <ul class="body-2">
              <li v-for="id in providersByNetworkID" :key="id" class="py-1">
                {{ id }}
              </li>
            </ul>
          </div>
        </template>
      </UiListItemExpand>

      <UiListItemExpand
        v-if="policiesList?.length || exclusion?.is_all_policies"
      >
        <span class="text-sm">
          Excluded {{ policiesList.length === 1 ? 'Policy' : 'Policies' }} ({{
            exclusion?.is_all_policies ? 'All Policies' : policiesList.length
          }})
        </span>

        <template #content>
          <div class="body-2 p-5 text-neutral-900">
            <p v-if="exclusion?.is_all_policies">All policies</p>
            <ul v-else class="body-2">
              <li
                v-for="policy in policiesList"
                :key="policy.id"
                class="flex flex-col py-1"
              >
                <NuxtLink
                  :to="`/policies/${policy.id}`"
                  class="btn btn-text inline-block text-xs"
                  data-analytics="exclusion-detail-policy-link"
                >
                  {{ policy.name }}
                </NuxtLink>
                <span class="py-2 text-neutral-600">
                  {{ policy.id }}
                </span>
              </li>
            </ul>
          </div>
        </template>
      </UiListItemExpand>

      <UiListItemExpand v-if="exclusion?.source_types?.length">
        <span class="text-sm">
          Excluded
          <UiPluralize
            class="text-sm"
            :count="exclusion.source_types.length"
            plural="Sources"
            singular="Source"
          />
          ({{ exclusion.source_types.length }})
        </span>
        <template #content>
          <ul class="py-3">
            <li
              v-for="(source, i) in exclusion.source_types as any[]"
              :key="i"
              class="px-5 py-2 text-xs"
            >
              {{ source.name || source.source_type_level_1.name }}
            </li>
          </ul>
        </template>
      </UiListItemExpand>

      <UiListItemExpand v-if="editTypesList?.length">
        <span class="text-sm">
          Excluded Logic Type{{ editTypesList.length > 1 ? 's' : '' }} ({{
            editTypesList.length
          }})
        </span>

        <template #content>
          <div class="body-2 p-5 text-neutral-900">
            <ul class="body-2">
              <li v-for="et in editTypesList" :key="et.edit_type" class="py-1">
                {{ et.edit_type }}
              </li>
            </ul>
          </div>
        </template>
      </UiListItemExpand>

      <UiListItemExpand v-if="procedureCodeList?.length">
        <span class="text-sm">
          Excluded CPT®/HCPCS Code{{
            procedureCodeList.length > 1 ? 's' : ''
          }}
          ({{ procedureCodeList.length }})
        </span>

        <template #content>
          <div class="body-2 p-5 text-neutral-900">
            <ul class="body-2">
              <li
                v-for="procedure in procedureCodeList"
                :key="procedure.procedure_code"
                class="py-1"
              >
                <UiRefDataCode
                  :code="procedure.procedure_code"
                  model="Procedure"
                />
              </li>
            </ul>
          </div>
        </template>
      </UiListItemExpand>

      <UiListItemExpand v-if="exclusion?.procedure_ranges?.length">
        <span class="text-sm">
          Excluded Procedure Code Ranges ({{
            exclusion.procedure_ranges.length
          }})
        </span>

        <template #content>
          <div class="body-2 p-5 text-neutral-900">
            <ul class="body-2">
              <li
                v-for="range in exclusion.procedure_ranges as any[]"
                class="py-1"
              >
                {{
                  'procedure_start' in range
                    ? `${range.procedure_start.code}-${range.procedure_end.code}`
                    : `${range.procedure_code_start}-${range.procedure_code_end}`
                }}
              </li>
            </ul>
          </div>
        </template>
      </UiListItemExpand>

      <UiListItemExpand v-if="(exclusion as any)?.ndc_codes?.length">
        <span class="text-sm">
          Excluded NDC Codes ({{ (exclusion as any).ndc_codes.length }})
        </span>

        <template #content>
          <div class="body-2 p-5 text-neutral-900">
            <ul class="body-2">
              <li
                v-for="code in (exclusion as any).ndc_codes"
                :key="code"
                class="py-1"
              >
                {{ typeof code === 'object' ? code.ndc_code : code }}
              </li>
            </ul>
          </div>
        </template>
      </UiListItemExpand>

      <UiListItemExpand v-if="posList?.length">
        <span class="text-sm">
          Excluded Place of Service Code{{ posList.length > 1 ? 's' : '' }} ({{
            posList.length
          }})
        </span>

        <template #content>
          <div class="body-2 p-5 text-neutral-900">
            <ul class="body-2">
              <li v-for="pos in posList" :key="pos.code" class="py-1">
                {{ pos.code }} - {{ pos.name }}
              </li>
            </ul>
          </div>
        </template>
      </UiListItemExpand>

      <UiListItemExpand v-if="exclusion?.modifiers?.length">
        <span class="text-sm">
          Excluded Modifiers ({{ exclusion.modifiers.length }})
        </span>

        <template #content>
          <div class="body-2 p-5 text-neutral-900">
            <ul class="body-2">
              <li
                v-for="modifier in exclusion.modifiers"
                :key="modifier as any"
                class="py-1"
              >
                <UiRefDataCode
                  :code="
                    typeof modifier === 'object'
                      ? modifier.modifier_code
                      : modifier
                  "
                  model="modifier"
                />
              </li>
            </ul>
          </div>
        </template>
      </UiListItemExpand>

      <UiListItemExpand v-if="pricingMethodologyCodesList?.length">
        <span class="text-sm">
          <UiPluralize
            :count="pricingMethodologyCodesList.length"
            plural="Excluded Pricing Methodology Codes"
            singular="Excluded Pricing Methodology Code"
          />
          ({{ pricingMethodologyCodesList.length }})
        </span>

        <template #content>
          <div class="body-2 p-5 text-neutral-900">
            <ul class="body-2">
              <li
                v-for="item in pricingMethodologyCodesList"
                :key="item.code"
                class="py-1"
              >
                <UiRefDataCode v-bind="item" model="PricingMethodologyCode" />
              </li>
            </ul>
          </div>
        </template>
      </UiListItemExpand>

      <UiListItemExpand v-if="typeOfBillCodesList?.length">
        <span class="text-sm">
          <UiPluralize
            :count="typeOfBillCodesList.length"
            plural="Excluded Type of Bill Codes"
            singular="Excluded Type of Bill Code"
          />
          ({{ typeOfBillCodesList.length }})
        </span>

        <template #content>
          <div class="body-2 p-5 text-neutral-900">
            <ul class="body-2">
              <li
                v-for="item in typeOfBillCodesList"
                :key="item.code"
                class="py-1"
              >
                <UiRefDataCode v-bind="item" model="TypeOfBill" />
              </li>
            </ul>
          </div>
        </template>
      </UiListItemExpand>

      <UiListItemExpand v-if="revenueCodesList?.length">
        <span class="text-sm">
          <UiPluralize
            :count="revenueCodesList.length"
            plural="Excluded Revenue Codes"
            singular="Excluded Revenue Code"
          />
          ({{ revenueCodesList.length }})
        </span>

        <template #content>
          <div class="body-2 p-5 text-neutral-900">
            <ul class="body-2">
              <li
                v-for="item in revenueCodesList"
                :key="item.code"
                class="py-1"
              >
                <UiRefDataCode v-bind="item" model="Revenue" />
              </li>
            </ul>
          </div>
        </template>
      </UiListItemExpand>

      <UiListItemExpand v-if="conditionCodesList?.length">
        <span class="text-sm">
          <UiPluralize
            :count="conditionCodesList.length"
            plural="Excluded Condition Codes"
            singular="Excluded Condition Code"
          />
          ({{ conditionCodesList.length }})
        </span>

        <template #content>
          <div class="body-2 p-5 text-neutral-900">
            <ul class="body-2">
              <li
                v-for="item in conditionCodesList"
                :key="item.code"
                class="py-1"
              >
                <UiRefDataCode v-bind="item" model="ConditionCode" />
              </li>
            </ul>
          </div>
        </template>
      </UiListItemExpand>

      <UiListItemExpand v-if="diagnosisList?.length">
        <span class="text-sm">
          Excluded Diagnosis Code{{ diagnosisList.length > 1 ? 's' : '' }} ({{
            diagnosisList.length
          }})
        </span>

        <template #content>
          <div class="body-2 p-5 text-neutral-900">
            <ul class="body-2">
              <li
                v-for="diagnosis in diagnosisList"
                :key="diagnosis.code"
                class="py-1"
              >
                <UiRefDataCode :code="diagnosis.code" model="Diagnosis" />
              </li>
            </ul>
          </div>
        </template>
      </UiListItemExpand>

      <UiListItemExpand v-if="exclusion?.mcd_provider_types?.length">
        <UiPluralize
          class="text-sm"
          :count="exclusion.mcd_provider_types.length"
          plural="Excluded Medicaid Provider Types"
          singular="Excluded Medicaid Provider Type"
        />

        <template #content>
          <div class="body-2 p-5 text-neutral-900">
            <ul class="body-2">
              <li
                v-for="code in exclusion.mcd_provider_types"
                :key="code"
                class="py-1"
              >
                {{ code }}
              </li>
            </ul>
          </div>
        </template>
      </UiListItemExpand>

      <UiListItemExpand v-if="exclusion?.diagnosis_ranges?.length">
        <span class="text-sm">
          Excluded Diagnosis Code Ranges ({{
            exclusion.diagnosis_ranges.length
          }})
        </span>

        <template #content>
          <div class="body-2 p-5 text-neutral-900">
            <ul class="body-2">
              <li
                v-for="range in exclusion.diagnosis_ranges as any[]"
                class="py-1"
              >
                {{
                  `${range.diagnosis_code_start}-${range.diagnosis_code_end}`
                }}
              </li>
            </ul>
          </div>
        </template>
      </UiListItemExpand>

      <UiListItemExpand v-if="providerSpecialtyCodesList?.length">
        <span class="text-sm">
          Excluded Provider Specialty Code{{
            providerSpecialtyCodesList.length > 1 ? 's' : ''
          }}
          ({{ providerSpecialtyCodesList.length }})
        </span>

        <template #content>
          <div class="body-2 p-5 text-neutral-900">
            <ul class="body-2">
              <li
                v-for="psCode in providerSpecialtyCodesList"
                :key="psCode"
                class="py-1"
              >
                {{ psCode }}
              </li>
            </ul>
          </div>
        </template>
      </UiListItemExpand>

      <UiListItemExpand v-if="exclusion?.ltss_indicators">
        <span class="text-sm">Excluded LTSS Indicator</span>

        <template #content>
          <div class="body-2 p-5 text-neutral-900">
            {{ exclusion?.ltss_indicators ? 'Yes' : 'No' }}
          </div>
        </template>
      </UiListItemExpand>

      <UiListItemExpand v-if="exclusion?.billing_provider_taxonomies?.length">
        <UiPluralize
          class="text-sm"
          :count="exclusion.billing_provider_taxonomies.length"
          plural="Excluded Billing Provider Taxonomy Codes"
          singular="Excluded Billing Provider Taxonomy Code"
        />

        <template #content>
          <div class="body-2 p-5 text-neutral-900">
            <ul class="body-2">
              <li
                v-for="code in exclusion.billing_provider_taxonomies"
                :key="code"
                class="py-1"
              >
                {{ code }}
              </li>
            </ul>
          </div>
        </template>
      </UiListItemExpand>

      <UiListItemExpand v-if="exclusion?.rendering_provider_taxonomies?.length">
        <UiPluralize
          class="text-sm"
          :count="exclusion.rendering_provider_taxonomies.length"
          plural="Excluded Rendering Provider Taxonomy Codes"
          singular="Excluded Rendering Provider Taxonomy Code"
        />

        <template #content>
          <div class="body-2 p-5 text-neutral-900">
            <ul class="body-2">
              <li
                v-for="code in exclusion.rendering_provider_taxonomies"
                :key="code"
                class="py-1"
              >
                {{ code }}
              </li>
            </ul>
          </div>
        </template>
      </UiListItemExpand>
    </div>
  </section>
</template>
